import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Mail, Sparkles, Send, Loader2, User, Bot, Mic, MicOff, X, BookOpen, Paperclip, FileText, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBrowserSpeechToText } from "@/hooks/useBrowserSpeechToText";
import { getCompatibleTags } from "@/lib/d2dTagRules";
import { processSignature } from "@/lib/signatureUtils";
import type { D2DTag } from "@/types/d2d";
import { SUPABASE_CONFIG, SUPABASE_FUNCTIONS_URL, SUPABASE_STORAGE_URL } from "@/lib/supabaseConfig";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface KnowledgeBaseEntry {
  title: string;
  content: string;
  attachments?: Array<{
    name: string;
    path: string;
    type: string;
    size?: number;
  }>;
}

interface Attachment {
  filename: string;
  content: string; // base64
  contentType: string;
  source?: string; // "knowledge-base" or "upload"
}

interface KTOfferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unitId: string;
  unitName?: string;
  currentEmail?: string;
  onOfferSent?: (email: string) => void;
  hasOfferSent?: boolean;
}

export function KTOfferDialog({
  open,
  onOpenChange,
  unitId,
  unitName = "",
  currentEmail = "",
  onOfferSent,
  hasOfferSent = false,
}: KTOfferDialogProps) {
  const { user } = useAuth();
  const [email, setEmail] = useState(currentEmail);
  const [showAICompose, setShowAICompose] = useState(false);
  const [emailBody, setEmailBody] = useState("");
  const [subject, setSubject] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [profile, setProfile] = useState<{ 
    organization_id?: string; 
    team_id?: string; 
    first_name?: string; 
    last_name?: string;
    phone?: string;
    email?: string;
    contact_email?: string;
    address?: string;
    avatar_url?: string;
  } | null>(null);
  const [organization, setOrganization] = useState<{
    name: string;
    logo_url?: string;
    color?: string;
  } | null>(null);
  
  // AI Compose states
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState("");
  const [knowledgeBaseContent, setKnowledgeBaseContent] = useState<KnowledgeBaseEntry[]>([]);
  const [loadingContext, setLoadingContext] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  
  // Attachments state
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [availableKBAttachments, setAvailableKBAttachments] = useState<Array<{
    entryTitle: string;
    name: string;
    path: string;
    type: string;
    size?: number;
  }>>([]);
  const [loadingAttachment, setLoadingAttachment] = useState<string | null>(null);

  const {
    isRecording,
    isSupported: isSpeechSupported,
    interimTranscript,
    toggleRecording,
  } = useBrowserSpeechToText((text) => {
    setInput(text);
  });

  // Extract family name from unit name (e.g., "Familie Müller" -> "Müller")
  const extractFamilyName = (name: string): string => {
    if (!name) return "";
    // Handle formats like "Familie Müller", "Fam. Müller", "Müller"
    const familyMatch = name.match(/(?:Familie|Fam\.?)\s+(.+)/i);
    if (familyMatch) return familyMatch[1].trim();
    return name.trim();
  };

  const familyName = extractFamilyName(unitName);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setEmail(currentEmail);
      setSubject("");
      setEmailBody("");
      setShowAICompose(false);
      setMessages([]);
      setInput("");
      setGeneratedEmail("");
      setAttachments([]);
      setAvailableKBAttachments([]);
      loadKnowledgeBase();
      loadProfile();
    }
  }, [open, currentEmail]);

  const loadProfile = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('profiles')
      .select('organization_id, team_id, first_name, last_name, phone, email, contact_email, address, avatar_url')
      .eq('user_id', user.id)
      .single();
    if (data) {
      setProfile(data);
      // Load organization data
      if (data.organization_id) {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('name, logo_url, color')
          .eq('id', data.organization_id)
          .single();
        if (orgData) {
          setOrganization(orgData);
        }
      }
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, generatedEmail]);

  const loadKnowledgeBase = async () => {
    if (!user) return;
    
    setLoadingContext(true);
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .single();

      if (profileData?.team_id) {
        const { data: kbData } = await supabase
          .from('team_knowledge_base')
          .select('title, content, attachments')
          .eq('team_id', profileData.team_id)
          .limit(10);

        if (kbData && kbData.length > 0) {
          const truncatedEntries = kbData.map(entry => ({
            title: entry.title,
            content: entry.content.length > 2000 
              ? entry.content.substring(0, 2000) + '...' 
              : entry.content,
            attachments: entry.attachments as KnowledgeBaseEntry['attachments']
          }));
          setKnowledgeBaseContent(truncatedEntries);
          
          // Collect all available attachments from KB
          const allAttachments: typeof availableKBAttachments = [];
          kbData.forEach(entry => {
            const attachmentsData = entry.attachments as Array<{
              name: string;
              path?: string;
              url?: string;
              type: string;
              size?: number;
            }> | null;
            if (attachmentsData && Array.isArray(attachmentsData)) {
              attachmentsData.forEach(att => {
                // Support both 'path' and 'url' formats from KB attachments
                const filePath = att.path || att.url;
                if (filePath) {
                  allAttachments.push({
                    entryTitle: entry.title,
                    name: att.name,
                    path: filePath,
                    type: att.type,
                    size: att.size
                  });
                }
              });
            }
          });
          setAvailableKBAttachments(allAttachments);
        }
      }
    } catch (error) {
      console.error('Error loading knowledge base:', error);
    } finally {
      setLoadingContext(false);
    }
  };

  // Add attachment from KB
  const addKBAttachment = async (kbAtt: typeof availableKBAttachments[0]) => {
    // Check if already added
    if (attachments.some(a => a.filename === kbAtt.name)) {
      toast.info("Anhang bereits hinzugefügt");
      return;
    }

    if (!kbAtt.path) {
      toast.error("Anhang-Pfad nicht gefunden");
      return;
    }

    setLoadingAttachment(kbAtt.name);
    try {
      let blob: Blob;
      
      // Check if it's a full URL or a storage path
      if (kbAtt.path.startsWith('http')) {
        // Fetch directly from URL
        const response = await fetch(kbAtt.path);
        if (!response.ok) throw new Error('Failed to fetch file');
        blob = await response.blob();
      } else {
        // Download from Supabase storage using path
        const { data, error } = await supabase.storage
          .from('knowledge-base-images')
          .download(kbAtt.path);

        if (error) throw error;
        blob = data;
      }

      // Convert to base64
      const arrayBuffer = await blob.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ''
        )
      );

      setAttachments(prev => [...prev, {
        filename: kbAtt.name,
        content: base64,
        contentType: kbAtt.type || 'application/pdf',
        source: 'knowledge-base'
      }]);

      toast.success(`"${kbAtt.name}" als Anhang hinzugefügt`);
    } catch (error) {
      console.error('Error loading KB attachment:', error);
      toast.error("Fehler beim Laden des Anhangs");
    } finally {
      setLoadingAttachment(null);
    }
  };

  const removeAttachment = (filename: string) => {
    setAttachments(prev => prev.filter(a => a.filename !== filename));
  };

  const streamAICompose = async (userMessage: string) => {
    setIsLoading(true);
    const newMessages: Message[] = [...messages, { role: "user", content: userMessage }];
    setMessages(newMessages);
    setInput("");

    let assistantContent = "";
    const userName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '';

    try {
      const response = await fetch(
        `${SUPABASE_FUNCTIONS_URL}/compose-email-ai`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${SUPABASE_CONFIG.anonKey}`,
          },
          body: JSON.stringify({
            messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
            originalEmail: null,
            userName,
            needsSubject: true,
            customerContext: familyName ? {
              lastName: familyName,
            } : null,
            knowledgeBaseContent: knowledgeBaseContent.length > 0 ? knowledgeBaseContent : null,
            availableAttachments: availableKBAttachments.length > 0 ? availableKBAttachments.map(a => ({
              name: a.name,
              entryTitle: a.entryTitle
            })) : null,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Fehler bei der KI-Anfrage");
      }

      if (!response.body) {
        throw new Error("Keine Antwort erhalten");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantContent += content;
              setGeneratedEmail(assistantContent);
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      // Parse subject from response if present
      let emailContent = assistantContent;
      const subjectMatch = assistantContent.match(/^BETREFF:\s*(.+?)(\r?\n|\r)/i);
      if (subjectMatch) {
        setSubject(subjectMatch[1].trim());
        emailContent = assistantContent.slice(subjectMatch[0].length).trim();
      }

      // Parse recommended attachments from response (just remove the line, don't auto-add)
      const attachmentsMatch = emailContent.match(/\n?ANHÄNGE:\s*(.+)$/i);
      if (attachmentsMatch) {
        // Remove the attachments line from the email content
        emailContent = emailContent.replace(/\n?ANHÄNGE:\s*(.+)$/i, '').trim();
      }

      setMessages((prev) => [...prev, { role: "assistant", content: emailContent }]);
      setGeneratedEmail(emailContent);
    } catch (error) {
      console.error("AI compose error:", error);
      toast.error(error instanceof Error ? error.message : "Ein Fehler ist aufgetreten");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAISend = () => {
    if (!input.trim() || isLoading) return;
    streamAICompose(input.trim());
  };

  const handleAcceptAI = () => {
    if (generatedEmail) {
      setEmailBody(generatedEmail);
      setShowAICompose(false);
    }
  };

  const handleSendOffer = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Bitte geben Sie eine gültige E-Mail-Adresse ein");
      return;
    }

    if (!emailBody.trim()) {
      toast.error("Bitte verfassen Sie eine Nachricht");
      return;
    }

    setIsSending(true);
    try {
      // Get user's email account
      const { data: emailAccounts } = await supabase
        .from('email_accounts')
        .select('*')
        .eq('user_id', user?.id)
        .eq('is_active', true)
        .limit(1);

      if (!emailAccounts || emailAccounts.length === 0) {
        toast.error("Kein E-Mail-Konto konfiguriert", {
          description: "Bitte richten Sie zuerst ein E-Mail-Konto ein."
        });
        return;
      }

      const account = emailAccounts[0];

      // Get organization signature and process placeholders
      let processedSignatureHtml = "";
      if (profile?.organization_id) {
        const { data: signatures } = await supabase
          .from('organization_signatures')
          .select('signature_html')
          .eq('organization_id', profile.organization_id)
          .eq('is_default', true)
          .limit(1);

        if (signatures && signatures.length > 0) {
          // Process signature with user and organization data to replace placeholders
          processedSignatureHtml = processSignature(
            signatures[0].signature_html,
            profile,
            organization
          );
        }
      }

      // Build sender name: "Organisationsname - Beratername"
      const advisorName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '';
      const senderDisplayName = organization 
        ? `${organization.name}${advisorName ? ` - ${advisorName}` : ''}`
        : advisorName;

      // Convert body to HTML with processed signature
      const bodyHtml = emailBody
        .split('\n')
        .map(line => line.trim() === '' ? '<br>' : `<p>${line}</p>`)
        .join('') + (processedSignatureHtml ? `<br><br>${processedSignatureHtml}` : '');

      // Build organization info for email template
      const orgInfo = organization ? {
        name: organization.name,
        logoUrl: organization.logo_url 
          ? (organization.logo_url.startsWith('http') 
              ? organization.logo_url 
              : `${SUPABASE_STORAGE_URL}/organization-logos/${organization.logo_url}`)
          : undefined,
        color: organization.color || '#1a1a2e',
      } : undefined;

      // Send email using user's SMTP credentials
      const { error: sendError } = await supabase.functions.invoke('send-email', {
        body: {
          to: email,
          subject,
          html: bodyHtml,
          text: emailBody,
          fromName: senderDisplayName,
          organization: orgInfo,
          replyTo: profile?.contact_email || account.email_address,
          attachments: attachments.length > 0 ? attachments.map(a => ({
            filename: a.filename,
            content: a.content,
            contentType: a.contentType
          })) : undefined,
          // Use user's connected email account SMTP credentials
          smtp: {
            host: account.smtp_host,
            port: account.smtp_port || 587,
            username: account.smtp_username || account.email_address,
            password: account.smtp_password,
          },
        },
      });

      if (sendError) {
        throw sendError;
      }

      // Save to emails table
      await supabase.from('emails').insert({
        user_id: user?.id,
        organization_id: profile?.organization_id,
        account_id: account.id,
        from_address: account.email_address,
        to_addresses: [email],
        subject,
        body_html: bodyHtml,
        body_text: emailBody,
        status: 'sent',
        sent_at: new Date().toISOString(),
      });

      // First, get current unit data to apply tag rules
      const { data: unitData } = await supabase
        .from('d2d_units')
        .select('tags')
        .eq('id', unitId)
        .single();

      const currentTags = (unitData?.tags || []) as D2DTag[];
      const newTags = getCompatibleTags(currentTags, 'KT');

      // Update the unit: save email, apply KT tag (which removes NA and adds HOT)
      const { error: updateError } = await supabase
        .from('d2d_units')
        .update({
          customer_email: email,
          tags: newTags,
          last_contact_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', unitId);

      if (updateError) {
        console.error('Error updating unit tags:', updateError);
      }

      // Add note about offer sent with subject
      await supabase.from('d2d_notes').insert({
        unit_id: unitId,
        user_id: user?.id,
        note: `📧 Angebot versendet\nAn: ${email}\nBetreff: ${subject}`,
      });

      toast.success("Angebot erfolgreich gesendet", {
        description: `E-Mail wurde an ${email} gesendet`
      });

      onOfferSent?.(email);
      onOpenChange(false);
    } catch (error) {
      console.error('Error sending offer:', error);
      toast.error("Fehler beim Senden", {
        description: "Das Angebot konnte nicht gesendet werden"
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-4 py-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Angebot versenden
            {familyName && (
              <span className="text-muted-foreground font-normal">
                – {familyName}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Email Input */}
          <div className="space-y-2">
            <Label htmlFor="offer-email">E-Mail-Adresse</Label>
            <Input
              id="offer-email"
              type="email"
              placeholder="kunde@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {/* Subject Input */}
          <div className="space-y-2">
            <Label htmlFor="offer-subject">Betreff</Label>
            <Input
              id="offer-subject"
              type="text"
              placeholder="Ihr persönliches Angebot"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          {/* Message Body */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="offer-body">Nachricht</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAICompose(true)}
                className="text-primary hover:text-primary/80"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Mit KI verfassen
              </Button>
            </div>
            <Textarea
              id="offer-body"
              placeholder="Schreiben Sie Ihre Nachricht..."
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              className="min-h-[200px] resize-none"
            />
          </div>

          {/* Attachments Section */}
          <div className="space-y-3">
            <Label>Anhänge</Label>
            
            {/* Current Attachments - Chips wie in E-Mails */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachments.map((att) => (
                  <div 
                    key={att.filename}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-xs group"
                  >
                    <FileText className="h-3 w-3 text-primary" />
                    <span className="max-w-[150px] truncate">{att.filename}</span>
                    <button
                      onClick={() => removeAttachment(att.filename)}
                      className="ml-0.5 opacity-60 hover:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Available KB Attachments - Gruppiert nach Wissensbank-Eintrag */}
            {availableKBAttachments.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium">Aus Wissensbank hinzufügen:</p>
                {/* Group attachments by entryTitle */}
                {Object.entries(
                  availableKBAttachments
                    .filter(kbAtt => !attachments.some(a => a.filename === kbAtt.name))
                    .reduce((acc, att) => {
                      const title = att.entryTitle || 'Sonstige';
                      if (!acc[title]) acc[title] = [];
                      acc[title].push(att);
                      return acc;
                    }, {} as Record<string, typeof availableKBAttachments>)
                ).map(([entryTitle, atts]) => (
                  <div key={entryTitle} className="space-y-1.5">
                    <p className="text-xs font-medium text-foreground">{entryTitle}</p>
                    <div className="flex flex-col gap-1">
                      {atts.map((kbAtt, idx) => (
                        <button
                          key={`${kbAtt.path}-${idx}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted/60 hover:bg-muted text-xs transition-colors disabled:opacity-50"
                          disabled={loadingAttachment === kbAtt.name}
                          onClick={() => addKBAttachment(kbAtt)}
                        >
                          {loadingAttachment === kbAtt.name ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Paperclip className="h-3 w-3 text-muted-foreground" />
                          )}
                          <span>{kbAtt.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {availableKBAttachments.length === 0 && attachments.length === 0 && (
              <div className="text-xs text-muted-foreground bg-muted/30 px-3 py-2.5 rounded-lg text-center">
                Keine PDF-Anhänge in der Wissensbank verfügbar
              </div>
            )}
          </div>

          {/* Knowledge Base Info */}
          {knowledgeBaseContent.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded-lg">
              <BookOpen className="h-3 w-3" />
              {knowledgeBaseContent.length} Wissensbank-Einträge verfügbar für KI
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleSendOffer}
            disabled={isSending || !email || !emailBody.trim()}
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Senden...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Angebot senden
              </>
            )}
          </Button>
        </div>
      </DialogContent>

      {/* AI Compose Dialog */}
      <Dialog open={showAICompose} onOpenChange={setShowAICompose}>
        <DialogContent className="sm:max-w-[600px] h-[70vh] sm:h-[80vh] flex flex-col p-0 [&>button]:hidden">
          <DialogHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Angebot mit KI verfassen
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowAICompose(false)}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>

          <div className="flex-1 flex flex-col min-h-0">
            {/* Context indicators */}
            {(knowledgeBaseContent.length > 0 || loadingContext || familyName) && (
              <div className="px-4 py-2 border-b bg-muted/30 flex flex-wrap gap-2 text-xs">
                {loadingContext ? (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Kontext laden...
                  </span>
                ) : (
                  <>
                    {familyName && (
                      <span className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        <User className="h-3 w-3" />
                        Familie {familyName}
                      </span>
                    )}
                    {knowledgeBaseContent.length > 0 && (
                      <span className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        <BookOpen className="h-3 w-3" />
                        {knowledgeBaseContent.length} Wissensbank-Einträge
                      </span>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Chat messages */}
            <ScrollArea className="flex-1 px-4 py-3" ref={scrollRef}>
              <div className="space-y-4">
                {messages.length === 0 && !isLoading && (
                  <div className="text-center text-muted-foreground py-8">
                    <Sparkles className="h-12 w-12 mx-auto mb-4 text-primary/50" />
                    <p className="text-sm">
                      Beschreiben Sie, was im Angebot stehen soll.
                    </p>
                    {familyName && (
                      <p className="text-xs mt-2 text-primary/70">
                        Das Angebot wird an Familie {familyName} adressiert.
                      </p>
                    )}
                    <p className="text-xs mt-1 text-muted-foreground/70">
                      z.B. "Erstelle ein Angebot für einen Glasfaser-Anschluss"
                    </p>
                  </div>
                )}

                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      "flex gap-3",
                      msg.role === "user" ? "justify-end" : "justify-start"
                    )}
                  >
                    {msg.role === "assistant" && (
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Bot className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <div
                      className={cn(
                        "rounded-lg px-4 py-2 max-w-[80%]",
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      )}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    {msg.role === "user" && (
                      <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                ))}

                {isLoading && generatedEmail && (
                  <div className="flex gap-3 justify-start">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                    <div className="rounded-lg px-4 py-2 max-w-[80%] bg-muted">
                      <p className="text-sm whitespace-pre-wrap">{generatedEmail}</p>
                      <span className="inline-block w-2 h-4 bg-primary/50 animate-pulse ml-0.5" />
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Input area */}
            <div className="p-4 border-t bg-background">
              {generatedEmail && !isLoading && (
                <div className="mb-3 flex justify-end">
                  <Button onClick={handleAcceptAI} size="sm">
                    Übernehmen
                  </Button>
                </div>
              )}
              <div className="flex gap-2">
                <Textarea
                  value={isRecording ? interimTranscript || input : input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleAISend();
                    }
                  }}
                  placeholder="Beschreiben Sie das gewünschte Angebot..."
                  className="min-h-[60px] max-h-[120px] resize-none"
                  disabled={isLoading}
                />
                <div className="flex flex-col gap-2">
                  {isSpeechSupported && (
                    <Button
                      variant={isRecording ? "destructive" : "outline"}
                      size="icon"
                      onClick={toggleRecording}
                      disabled={isLoading}
                      className="shrink-0"
                    >
                      {isRecording ? (
                        <MicOff className="h-4 w-4" />
                      ) : (
                        <Mic className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    size="icon"
                    onClick={handleAISend}
                    disabled={!input.trim() || isLoading}
                    className="shrink-0"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
